import { STATIC_DATASET_USAGE_KEY } from '@/utils/queryKeys.ts';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  AUTOMATION_LATEST_VERSION_KEY,
  AUTOMATION_VERSIONS_KEY,
  OUTPUT_CONTRACTS_KEY,
} from '@/utils/queryKeys.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useMatch } from 'react-router-dom';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import useRetrieveAutomation from '@/hooks/api/automations/useRetrieveAutomation.ts';
import EditVersionContext from '@/context/EditVersionContext/EditVersionContext.tsx';
import { EditVersionRequest, StaleDraftState } from '@/context/EditVersionContext/types.ts';
import { AutomationVersion } from '@/types/automations.ts';
import {
  getBuilderVersionErrorPayload,
  normalizeSnapshotVersionId,
} from '@/services/builderVersionErrors.ts';

export const EditVersionProvider = ({ children }: { children: ReactNode }) => {
  const [queue, setQueue] = useState<EditVersionRequest<unknown, unknown>[]>([]);
  const queueRef = useRef(queue);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editorVersionId, setEditorVersionId] = useState<string | null>(null);
  const [editingVersion, setEditingVersion] = useState<AutomationVersion | null>(null);
  const [staleDraft, setStaleDraft] = useState<StaleDraftState | null>(null);
  const isEditorPage = !!useMatch('/automations/:automationId/editor');
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const hasPendingEdits = queue.length > 0 || isProcessing;
  const [replacements, setReplacements] = useState(new Set<string>());

  const addToQueue = useCallback(
    <AutomationVersionDetailed, AxiosError>(
      request: EditVersionRequest<AutomationVersionDetailed, AxiosError>,
    ) => {
      if (staleDraft) {
        notifications.show({
          title: 'Latest draft changed',
          message: 'Reload the editor before making more step changes.',
          color: 'red',
        });
        request.onComplete?.();
        return;
      }
      queueRef.current = [...queueRef.current, request as EditVersionRequest<unknown, unknown>];
      setQueue(queueRef.current);
    },
    [staleDraft],
  );

  const queryClient = useQueryClient();

  const { data: automation } = useRetrieveAutomation();
  const automationId = useMemo(() => automation?.id, [automation?.id]);

  const reportLatestDraftChanged = useCallback(
    (latestDraftVersionId: string | null) => {
      setStaleDraft((currentStaleDraft) => ({
        editorVersionId: currentStaleDraft?.editorVersionId ?? editorVersionId,
        latestVersionId: latestDraftVersionId,
      }));

      if (automationId) {
        void queryClient.invalidateQueries({
          queryKey: AUTOMATION_LATEST_VERSION_KEY(automationId),
        });
      }
    },
    [automationId, editorVersionId, queryClient],
  );

  const reloadEditor = useCallback(() => {
    window.location.reload();
  }, []);

  const registerReplacement = useCallback((operationId: string) => {
    setReplacements((current) => new Set(current).add(operationId));
  }, []);
  const isRegisteredReplacement = useCallback(
    (operationId: string) => replacements.has(operationId),
    [replacements],
  );
  const acceptReplacement = useCallback(
    (operationId: string, version: AutomationVersion) => {
      if (
        !replacements.has(operationId) ||
        hasPendingEdits ||
        queueRef.current.length > 0 ||
        !automationId ||
        version.source_version_id !== editorVersionId ||
        (latestVersion?.id !== editorVersionId && latestVersion?.id !== version.id)
      )
        return false;
      setReplacements((current) => {
        const next = new Set(current);
        next.delete(operationId);
        return next;
      });
      setEditorVersionId(version.id);
      setEditingVersion(version);
      setStaleDraft(null);
      queryClient.setQueryData(AUTOMATION_LATEST_VERSION_KEY(automationId), version);
      void queryClient.invalidateQueries({ queryKey: AUTOMATION_VERSIONS_KEY(automationId) });
      void queryClient.invalidateQueries({ queryKey: ['static-version-resources'] });
      return true;
    },
    [replacements, hasPendingEdits, automationId, editorVersionId, latestVersion?.id, queryClient],
  );

  useEffect(() => {
    if (!isEditorPage) {
      setReplacements(new Set());
      queueRef.current = [];
      setQueue([]);
      setEditorVersionId(null);
      setEditingVersion(null);
      setStaleDraft(null);
      setIsProcessing(false);
      return;
    }
  }, [isEditorPage]);

  useEffect(() => {
    if (!isEditorPage) {
      return;
    }

    if (!latestVersion?.id) {
      return;
    }

    setEditorVersionId((currentEditorVersionId) => {
      if (!currentEditorVersionId) {
        setEditingVersion(latestVersion);
        return latestVersion.id;
      }

      if (currentEditorVersionId === latestVersion.id) {
        return currentEditorVersionId;
      }

      if (isProcessing) {
        return currentEditorVersionId;
      }

      setStaleDraft((currentStaleDraft) => ({
        editorVersionId: currentStaleDraft?.editorVersionId ?? currentEditorVersionId,
        latestVersionId: latestVersion.id,
      }));

      return currentEditorVersionId;
    });
    // The snapshot is immutable for a version id. Depending on the entire
    // query object would retrigger this state transition for equivalent cache
    // values and can create a render loop with non-memoized query adapters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorVersionId, isEditorPage, isProcessing, latestVersion?.id]);

  useEffect(() => {
    if (queue.length > 0 && !isProcessing && latestVersion?.id && automationId) {
      setIsProcessing(true);
      const { mutationFn, onSuccess, onError, onComplete } = queue[0];
      (async () => {
        let hasSnapshotConflict = false;
        try {
          const data = await mutationFn(latestVersion);
          onSuccess(data);
          queryClient.setQueryData(AUTOMATION_LATEST_VERSION_KEY(automationId), data);
          const updatedVersionId = (data as { id?: string })?.id ?? latestVersion.id;
          setEditorVersionId(updatedVersionId);
          if (isAutomationVersion(data)) {
            setEditingVersion(data);
          }
          await queryClient.invalidateQueries({
            queryKey: OUTPUT_CONTRACTS_KEY(automationId, updatedVersionId),
          });
          await queryClient.invalidateQueries({ queryKey: AUTOMATION_VERSIONS_KEY(automationId) });
          void queryClient.invalidateQueries({ queryKey: STATIC_DATASET_USAGE_KEY(automationId) });
        } catch (error) {
          const payload = getBuilderVersionErrorPayload(error);
          hasSnapshotConflict = payload?.code === 'builder_snapshot_mismatch';
          if (hasSnapshotConflict) {
            setStaleDraft((currentStaleDraft) => ({
              editorVersionId:
                currentStaleDraft?.editorVersionId ?? editorVersionId ?? latestVersion.id,
              latestVersionId: normalizeSnapshotVersionId(
                payload?.details.actual_latest_version_id,
              ),
            }));
          }
          onError(error);
        } finally {
          if (hasSnapshotConflict) {
            const cancelled = queueRef.current.slice(1);
            queueRef.current = [];
            setQueue([]);
            cancelled.forEach((request) => request.onComplete?.());
          } else {
            queueRef.current = queueRef.current.slice(1);
            setQueue(queueRef.current);
          }
          setIsProcessing(false);
          onComplete?.();
        }
      })();
    }
  }, [queue, isProcessing, latestVersion, queryClient, automationId, editorVersionId]);

  return (
    <EditVersionContext.Provider
      value={{
        addToQueue,
        registerReplacement,
        isRegisteredReplacement,
        acceptReplacement,
        isProcessing,
        hasPendingEdits,
        staleDraft,
        reportLatestDraftChanged,
        reloadEditor,
        editingVersion,
      }}
    >
      {children}
    </EditVersionContext.Provider>
  );
};

const isAutomationVersion = (value: unknown): value is AutomationVersion =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { id?: unknown }).id === 'string' &&
  Array.isArray((value as { schemas?: unknown }).schemas) &&
  Array.isArray((value as { steps?: unknown }).steps);
